import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { useLang } from "@/i18n/LanguageContext";
import Logo from "@/components/Logo";

export default function AdminLoginPage() {
  const { token, login, checking } = useAdminAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  if (checking) return <div className="min-h-screen bg-cream-100 grid place-items-center text-olive-800/60">…</div>;
  if (token) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/admin");
    } catch {
      setError(t("admin.invalid_creds"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="admin-login" className="min-h-screen bg-cream-100 grid place-items-center px-6">
      <div className="w-full max-w-md border border-cream-400 bg-cream-100 p-8 sm:p-10">
        <div className="mb-8">
          <Logo />
        </div>
        <span className="text-[11px] uppercase tracking-[0.32em] text-gold-deep font-medium">
          {t("admin.login_title")}
        </span>
        <h1 className="mt-3 font-serif text-3xl text-olive-900">{t("admin.login_sub")}</h1>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-[0.28em] text-olive-800/60">
              {t("admin.email")}
            </label>
            <input
              data-testid="admin-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full bg-cream-200 border border-cream-400 px-4 py-3 text-olive-900 text-sm focus:outline-none focus:border-olive-700"
              required
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.28em] text-olive-800/60">
              {t("admin.password")}
            </label>
            <input
              data-testid="admin-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full bg-cream-200 border border-cream-400 px-4 py-3 text-olive-900 text-sm focus:outline-none focus:border-olive-700"
              required
            />
          </div>
          {error && <div data-testid="admin-login-error" className="text-sm text-red-700">{error}</div>}
          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full px-6 py-4 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800 disabled:opacity-60"
          >
            {t("admin.login")}
          </button>
        </form>
      </div>
    </main>
  );
}
